import { useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";

import Modal from "components/Modal";
import Button from "components/Button";
import usePhase from "utils/usePhase";
import validate from "utils/validate";
import sendDraftSummary from "utils/sendDraftSummary";

import styles from "./SendEmailModal.module.css";
import summaryStyles from "./summary.module.css";

import constants from "constants/fi.json";

interface Props {
  hash: string;
  onClose: () => void;
  onSuccess: () => void;
}

const SendEmailModal = ({ hash, onClose, onSuccess }: Props) => {
  const t = usePhase("summary");

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [recipients, setRecipients] = useState<string[]>([""]);
  const [sending, setSending] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const headers = new Headers();
    if (process.env.REACT_APP_API_AUTH) {
      headers.set("Authorization", "Basic " + btoa(process.env.REACT_APP_API_AUTH));
    }

    const params = new URLSearchParams({ hash });
    const url = [
      process.env.REACT_APP_API_BASE,
      "wp-json", "memoria", "v1", "draft-pdf",
      `?${params.toString()}`,
    ].join("/");

    fetch(url, { headers, credentials: "include" })
      .then((res) => res.blob())
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;
        setPdfUrl(objectUrl);
        setPdfLoading(false);
      })
      .catch(() => setPdfLoading(false));

    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [hash]);

  const updateRecipient = (index: number, value: string) => {
    setRecipients((prev) => prev.map((v, i) => (i === index ? value : v)));
  };

  const removeRecipient = (index: number) => {
    setRecipients((prev) => prev.filter((_, i) => i !== index));
  };

  const addRecipient = () => {
    if (recipients.length < 10) setRecipients((prev) => [...prev, ""]);
  };

  const validRecipients = recipients.filter((r) => r.trim() && validate.email(r.trim()));

  const handleSend = () => {
    setSending(true);
    sendDraftSummary(hash, validRecipients, (result) => {
      setSending(false);
      if (result.success) onSuccess();
    });
  };

  return (
    <AnimatePresence>
      <Modal closeModal={onClose}>
        <p className={styles.description}>{t("pdf_download_description")}</p>

        <div className={summaryStyles.content}>
          <h3 className={summaryStyles.subtitle}>{t("additional_recipients")}</h3>
        </div>
        {recipients.map((value, index) => (
          <div key={index} className={[summaryStyles.row, styles.recipientRow].join(" ")}>
            <div className={summaryStyles.key}>{constants.recipient}</div>
            <input
              type="email"
              value={value}
              onChange={(e) => updateRecipient(index, e.target.value)}
              className={[
                summaryStyles.editingValue,
                styles.recipientInput,
                value && !validate.email(value) ? styles.error : "",
              ].join(" ")}
            />
            <button
              className={[styles.removeButton, styles.button].join(" ")}
              onClick={() => removeRecipient(index)}
              aria-label={constants.remove_recipient}
            >
              <span className={styles.icon}>&times;</span>
            </button>
          </div>
        ))}
        {recipients.length < 10 && (
          <button className={[styles.addButton, styles.button].join(" ")} onClick={addRecipient}>
            <span className={styles.icon}>+</span> {constants.add_recipient}
          </button>
        )}

        <div className={styles.actions}>
          {(!pdfLoading && pdfUrl &&
          <a href={pdfUrl} target="_blank" rel="noreferrer" className={styles.button}>
            {t("download_pdf")}
          </a>
          )}
          <Button onClick={handleSend} disabled={sending}>
            {t("send_pdf_to_email")}
          </Button>
        </div>
      </Modal>
    </AnimatePresence>
  );
};

export default SendEmailModal;
